import { useEffect, useMemo, useState } from 'react';

import { useAppState } from '../../../AppStateProvider';
import type { Company } from '../../../domain/company';
import { useCompaniesQuery } from '../../../features/company/useCompaniesQuery';
import { useCompanyQuotaQuery } from '../../../features/company/useCompanyQuotaQuery';
import { useNotifyCompanyViewMutation } from '../../../features/company/useNotifyCompanyViewMutation';
import { useFilterCollectedDomains } from '../../../hooks/useFilterCollectedDomains';
import { appStore } from '../../../services/appStore';

import { sortCompaniesByName } from './utils';

const getDomainOptions = (companiesData: Company[]) =>
  companiesData.map((company) => ({
    domain: company.domain,
    name: company.name,
  }));

export const useMultipleDomainsContainer = () => {
  const { companyDomain, collectedDomains } = useAppState();
  const filteredDomains = useFilterCollectedDomains();
  const companyQuota = useCompanyQuotaQuery().data;
  const { mutate } = useNotifyCompanyViewMutation();

  const companiesQuery = useCompaniesQuery();
  const companiesData = companiesQuery.data || [];

  const domainOptions = useMemo(() => getDomainOptions(companiesData), [companiesData, filteredDomains]);

  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(companyDomain);

  const handleSelectDomain = (domain: string) => {
    setSelectedDomain(domain);
    setIsDropdownOpened(false);
    appStore.setCompanyDomain(domain);
    if (companyQuota && !companyQuota.isUnlimited) {
      mutate(domain);
    }
  };

  useEffect(() => {
    setIsDropdownOpened(false);
  }, [collectedDomains]);

  useEffect(() => {
    if (companyDomain !== selectedDomain) {
      setSelectedDomain(companyDomain);
    }
  }, [companyDomain]);

  useEffect(() => {
    if (filteredDomains && !selectedDomain) {
      const sortedDomains = sortCompaniesByName(companiesData);
      setSelectedDomain(sortedDomains[0].domain);
      appStore.setCompanyDomain(sortedDomains[0].domain);
    }
  }, [filteredDomains, companyDomain]);

  return {
    selectedDomain,
    selectedDomainName: companiesData.find((company) => company.domain === selectedDomain)?.name || '',
    isDropdownOpened,
    setIsDropdownOpened,
    domainOptions,
    onSelectDomain: handleSelectDomain,
  };
};
